import React, { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Link } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { makeStyles } from '@mui/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { add, sub } from 'date-fns';
import { Context } from '../../Stores/EventInfoStore';
import OverviewGraph from './OverviewGraph/OverviewGraph';
import SoldItems from './SoldItems';
import ParticipantData from './ParticipantData';
import EventPages from './EventPages';
import EventTimeline from './EventTimeline';
import Review from './Review';
import StepTitle from '../Common/PageLayout/StepTitle';
import ReactivateBanner from '../ManageEvents/ReactivateEvent/ReactivateBanner';
import { formatApiDateTime } from '../../Format';
import { scrollToTop } from '../../Utils/scrollUtils';
import { publish } from '../../Consts/eventStatus';
import { formatEventStartDate } from '../../Utils/dateUtils';
import { questions } from '../../Consts/defaultQuestionNames';

import { useAuth } from '../../Contexts/AuthContext';
import GenericModal from '../Common/GenericModal/GenericModal';
import { Emails } from '../../Utils/urls';
import { wmPalette } from '../../Theme/WorldSportsGroupTheme';
import {
  PagePermissions,
  PermissionLevels,
} from '../../Consts/routePermissions';

const useStyles = makeStyles((theme) => ({
  paddingZero: {
    padding: 0,
  },
  paddingEight: {
    padding: 8,
  },
  emailLink: {
    color: wmPalette.black[90],
    textDecoration: 'underline',
    [theme.breakpoints.down('md')]: {
      wordBreak: 'break-all',
    },
  },
}));

const DashboardBase = () => {
  const { eventId, editionId } = useParams();
  const [eventInfo] = useContext(Context);
  const { isAdmin } = useAuth();
  const [blockEditModal, toggleBlockEditModal] = useState(
    eventInfo.permissionLevel !== PermissionLevels.defaultLevel
  );
  const classes = useStyles();

  const modalDetails = {
    admin: {
      title: 'Warning!',
      content: (
        <>
          This event is utilizing an integration with external platform. Changes
          to the existing configuration and content should not be made unless
          absolutely necessary, and may require an involvement of a developer.
          If you are uncertain about making any updates, please consult a team
          member for guidance
        </>
      ),
    },
    organiser: {
      title: 'Limited Functionality',
      content: (
        <>
          Some features of this application are currently inaccessible due to
          integrations with external platforms. Changes to your listing require
          administrative privileges.
          <br />
          Please contact us at{' '}
          <Link
            className={classes.emailLink}
            href={`mailto:${Emails.CustomerSuccess}`}
          >
            {' '}
            {Emails.CustomerSuccess}
          </Link>{' '}
          for information on how you can update your listing. We apologize for
          any inconvenience this may cause and appreciate your understanding.
        </>
      ),
    },
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const needsReactivate =
    eventInfo.isActiveEdition &&
    new Date(eventInfo?.basicInfo?.eventEndDate) < new Date();
  const hasNonbinaryGenderQuestion = eventInfo.customQuestions.some(
    (q) => q.presetQuestionName === questions.gender.questionName
  );

  const getInititalStartDate = () => {
    if (eventInfo.firstOrderDate !== null) {
      return formatApiDateTime(
        sub(new Date(eventInfo.firstOrderDate), { days: 1 })
      );
    } else {
      return formatApiDateTime(
        new Date(eventInfo.basicInfo.eventSaleStartDate)
      );
    }
  };

  const getInitialEndDate = () => {
    const today = new Date();
    const eventEndDate = add(new Date(eventInfo.basicInfo.eventEndDate), {
      days: 1,
    });
    if (new Date() < new Date(eventInfo.basicInfo.eventEndDate)) {
      return formatApiDateTime(today);
    } else {
      return formatApiDateTime(eventEndDate);
    }
  };

  const startDate = getInititalStartDate();
  const endDate = getInitialEndDate();
  eventInfo.basicInfo.eventStartDate = formatEventStartDate(
    eventInfo.basicInfo
  );
  React.useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <>
      <StepTitle title="Dashboard" />
      <Box style={{ maxWidth: '1500px' }}>
        {needsReactivate && <ReactivateBanner isNav={false} />}
        <Grid
          container
          direction={isMobile ? 'column' : 'row'}
          xs={12}
          spacing={2}
          className={classes.paddingZero}
        >
          <Grid
            container
            xs={12}
            lg={9}
            spacing={2}
            className={classes.paddingEight}
          >
            <Grid xs={12}>
              <OverviewGraph
                eventId={eventId}
                editionId={editionId}
                eventInfo={eventInfo}
                startDate={startDate}
                endDate={endDate}
              />
            </Grid>
            <Grid xs={12}>
              <SoldItems
                eventId={eventId}
                editionId={editionId}
                currency={eventInfo.basicInfo.eventCurrency}
                eventTickets={eventInfo.tickets}
                eventAddons={eventInfo.addOns}
                underReview={eventInfo.underReview}
                startDate={startDate}
                blockOrderManagement={
                  !isAdmin &&
                  (PagePermissions?.Orders?.blockedPermissions?.includes(
                    eventInfo.permissionLevel
                  ) ||
                    false)
                }
              />
            </Grid>
            <Grid xs={12}>
              <ParticipantData
                eventId={eventId}
                editionId={editionId}
                eventInfo={eventInfo}
                underReview={eventInfo.underReview}
                startDate={startDate}
                endDate={endDate}
                hasNonbinaryGenderQuestion={hasNonbinaryGenderQuestion}
              />
            </Grid>
            {!isMobile && eventInfo.eventPageStatus === publish && (
              <Grid xs={12}>
                <EventPages
                  eventId={eventId}
                  eventTitle={eventInfo.basicInfo.eventTitle}
                  permalink={eventInfo.permalink}
                />
              </Grid>
            )}
          </Grid>
          <Grid
            container
            xs={12}
            lg={3}
            direction="row"
            alignContent="flex-start"
            spacing={2}
            className={classes.paddingEight}
          >
            <Grid xs={12}>
              <EventTimeline eventInfo={eventInfo} />
            </Grid>
            <Grid xs={12}>
              <Review
                eventId={eventId}
                editionId={editionId}
                underReview={eventInfo.underReview}
              />
            </Grid>
            {isMobile && eventInfo.eventPageStatus === publish && (
              <Grid xs={12}>
                <EventPages
                  eventId={eventId}
                  eventTitle={eventInfo.basicInfo.eventTitle}
                  permalink={eventInfo.permalink}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>
      {blockEditModal && (
        <GenericModal
          open={blockEditModal}
          handleClose={() => toggleBlockEditModal(false)}
          closeText="Got It"
          {...modalDetails[isAdmin ? 'admin' : 'organiser']}
        />
      )}
    </>
  );
};

export default DashboardBase;
